import React from 'react';
import classNames from 'classnames';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { resolveId } from '@wix/communities-blog-client-common';
import { PostFooter } from '@app/external/common/components/post-footer';
import PostListItemCategoryLabel from '@app/external/common/components/post-list-item-category-label';
import PostListItemCover from '@app/external/common/components/post-list-item-cover';
import PostListItemHeader from '@app/external/common/components/post-list-item-header';
import PostTitle from '@app/external/common/components/post-title';
import RatingsDisplay, {
  RatingsDisplayLayout,
} from '@app/external/common/components/ratings-display';
import { useSelector } from '@app/external/common/components/runtime-context';
import { HorizontalSeparatorForPostCard } from '@app/external/common/components/separator';
import { useFeedMetadataSettings } from '@app/external/common/hooks/use-feed-metadata-settings';
import useFontClassName from '@app/external/common/hooks/use-font-class-name';
import useIsFeedDesignEnabled from '@app/external/common/hooks/use-is-feed-design-enabled';
import usePermissions from '@app/external/common/hooks/use-permissions';
import { usePostBorderWidth } from '@app/external/common/hooks/use-post-border-width';
import usePostFontSize from '@app/external/common/hooks/use-post-font-size';
import {
  getIsMoreButtonEnabled,
  getMobileContentAlignment,
} from '@app/external/common/selectors/app-settings-selectors';
import { getSection } from '@app/external/common/selectors/section-selectors';
import { getContentAlignmentStyles } from '@app/external/common/services/content-alignment-helpers';
import { getFeedColorClassName } from '@app/external/common/services/layout-config';
import { getPostActions } from '@app/external/common/services/post-actions';
import {
  getPostAverageRating,
  getPostTotalRatings,
} from '@app/external/common/store/post-ratings/post-ratings-selector';
import alignmentStyles from '@app/external/common/styles/post-list-item-alignment.scss';
import postListItemStyles from '@app/external/common/styles/post-list-item.scss';
import { type LayoutConfigItemComponentProps } from '../../post-list/layout-config-component-props';
import styles from './text-on-image.scss';

const PostListItemTOIMobile: React.FC<LayoutConfigItemComponentProps> = ({
  itemConfig,
  post,
  type,
}) => {
  const { can, canSee } = usePermissions();
  const { contentFontClassName, titleFontClassName } = useFontClassName();
  const { isRTL } = useEnvironment();
  const section = useSelector((state) => getSection(state));
  const {
    showCategoryLabel,
    showPostRating,
    isMetadataFooterVisible = true,
  } = useFeedMetadataSettings({ section, post });
  const { getPostClassName } = useIsFeedDesignEnabled(section);
  const { borderWidth } = usePostBorderWidth({ section });

  const { postTitleFontSize, postMetadataFontSize } = usePostFontSize(section!);

  const { alignment, isMoreButtonEnabled, postAverageRating, postTotalRatings } = useSelector(
    (state) => {
      const contentAlignmentMobile = getMobileContentAlignment(state, isRTL);
      const postActions = getPostActions({
        post,
        canSee,
        enableShare: true,
      });

      return {
        isMoreButtonEnabled: getIsMoreButtonEnabled(state, postActions.length > 0),
        alignment: getContentAlignmentStyles(contentAlignmentMobile, isRTL),
        postAverageRating: getPostAverageRating(state, resolveId(post)),
        postTotalRatings: getPostTotalRatings(state, resolveId(post)),
      };
    },
  );

  const postLink = `/${post.slug}`;

  const containerClassName = classNames(
    styles.container,
    styles[section!],
    alignmentStyles[alignment],
    contentFontClassName,
    'blog-text-color',
    'blog-card-background-color',
    'blog-card-border-color',
    'post-list-item',
    getPostClassName(
      'border-color',
      'post-container',
      getFeedColorClassName(type, 'background-color'),
    ),
  );

  const contentWrapperClassName = classNames(
    styles.contentWrapper,
    alignmentStyles[alignment],
    isRTL && alignmentStyles.isRTL,
    'post-list-item-wrapper',
    getPostClassName(
      'overlay-background-color',
      'description-font',
      getFeedColorClassName(type, 'description-color'),
      getFeedColorClassName(type, 'description-fill'),
    ),
  );

  const linkClassName = classNames(
    'post-title__text',
    styles.link,
    getPostClassName('link-hashtag-hover-color'),
  );
  const titleClassName = classNames(
    styles.title,
    titleFontClassName,
    getPostClassName('title-font', getFeedColorClassName(type, 'title-color')),
  );
  const headerClassName = classNames(
    styles.header,
    contentFontClassName,
    getPostClassName('description-font'),
    alignmentStyles.headerContainer,
  );
  const footerClassName = classNames(
    getPostClassName('description-font', getFeedColorClassName(type, 'description-color')),
  );
  const titleStyle = { fontSize: postTitleFontSize };
  const lineCount = postTitleFontSize >= 24 ? 3 : 4;

  return (
    <article className={containerClassName} style={{ borderWidth }} data-hook="post-list-item">
      <PostListItemCover
        post={post}
        postLink={postLink}
        type={type}
        canPlayVideo
        isPublic={can('share', 'post', post)}
        videoClassName={styles.videoEmbed}
        imageClassName={styles.image}
      />
      <div className={contentWrapperClassName}>
        <div className={classNames(styles.flexColReversed, styles.flexSpaceBetween)}>
          <div className={styles.flexColReversed}>
            <div className={alignmentStyles.textAlign}>
              <PostTitle
                type={type}
                title={post.title}
                style={titleStyle}
                className={titleClassName}
                to={postLink}
                linkClassName={linkClassName}
                lineCount={isMetadataFooterVisible ? lineCount : lineCount + 1}
              />

              {showPostRating && postTotalRatings ? (
                <RatingsDisplay
                  className={classNames(
                    postListItemStyles.rating,
                    postListItemStyles[section!],
                    styles.rating,
                    styles[type],
                    alignmentStyles.rating,
                    [styles.withTextOnImageStyles],
                  )}
                  rating={postAverageRating}
                  count={postTotalRatings}
                  layout={RatingsDisplayLayout.with_range}
                />
              ) : null}
            </div>
            {showCategoryLabel && (
              <PostListItemCategoryLabel
                className={alignmentStyles.categoryLabel}
                post={post}
                postListLayout={type}
              />
            )}
          </div>
          <div>
            <PostListItemHeader
              className={headerClassName}
              post={post}
              showMoreButton={isMoreButtonEnabled}
              showProfileImage={false}
              style={{ fontSize: postMetadataFontSize }}
              type={type}
            />
          </div>
        </div>
        {isMetadataFooterVisible && (
          <div style={{ fontSize: postMetadataFontSize }}>
            <HorizontalSeparatorForPostCard className={classNames(styles.separator)} />
            <PostFooter
              className={footerClassName}
              displayIcons={itemConfig.displayFooterIcons}
              post={post}
              type={type}
            />
          </div>
        )}
      </div>
    </article>
  );
};

export default PostListItemTOIMobile;
